import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  TouchEvent as ReactTouchEvent,
} from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { DateTime } from 'luxon'
import { Case, Switch, When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildScore } from '@/atoms/GuildScore'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlayCircleOutlineIcon } from '@/atoms/Icons/PlayCircleOutlineIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { ClosedCaptionsBadge, ContentRatingBadge, HighDefinitionBadge } from '@/atoms/MetadataBadge'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CaptionMD, CaptionSM, LabelMD } from '@/atoms/Text'
import { paths } from '@/constants'
import { projectTypes } from '@/constants/projectTypes'
import { BoundedImage } from '@/molecules/BoundedImage'
import { HeroBannerData } from '@/services/RecommendedBannerService/types'
import { useRecommendationTracking } from '@/services/RecommendedBannerService/useRecommendationTracking'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { useWatchPageContext } from '../WatchPageContext'

interface RecommendedBannerCarouselProps {
  heroBanners: HeroBannerData[]
  className?: string
  trackImpressions?: (recommendationId: string) => void
}

export const RecommendedBannerCarouselFull: React.FC<RecommendedBannerCarouselProps> = ({
  heroBanners,
  className,
  trackImpressions,
}) => {
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 200
  const contentRef = useRef<HTMLDivElement>(null)
  const [currentSlide, setCurrentSlide] = useState(0)
  const { t } = useTranslate('watch')
  const { trackRecommendation } = useRecommendationTracking()
  const { setCurrentBanner } = useWatchPageContext()

  const trackClick = useCallback(
    (recommendationId: string) => {
      trackRecommendation({ type: 'PRESS', items: [{ id: recommendationId! }] })
    },
    [trackRecommendation],
  )

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === heroBanners.length - 1 ? 0 : prev + 1))
  }, [heroBanners.length])

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev === 0 ? heroBanners.length - 1 : prev - 1))
  }, [heroBanners.length])

  const onTouchStart = (event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0].clientX)
  }

  const onTouchMove = (event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0].clientX)

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd

    if (distance > minSwipeDistance) {
      nextSlide()
    } else if (distance < -minSwipeDistance) {
      prevSlide()
    }
  }, [touchStart, touchEnd, nextSlide, prevSlide])

  useEffect(() => {
    if (trackImpressions) {
      trackImpressions(heroBanners[currentSlide]?.recommendationId)
    }
  }, [currentSlide, heroBanners, trackImpressions])

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentSlide === (heroBanners?.length ?? 0) - 1) {
        setCurrentSlide(0)
      } else {
        setCurrentSlide(currentSlide + 1)
      }
    }, 6500)
    return () => clearInterval(interval)
  }, [currentSlide, heroBanners?.length])

  useEffect(() => {
    setCurrentBanner(heroBanners[currentSlide])
  }, [currentSlide, heroBanners, setCurrentBanner])

  const ctaButtonProps = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return { ctaText: '', icon: null, href: '/' }

    const hasGuildAction = (actions?: Array<{ __typename?: string | null } | null | undefined> | null): boolean =>
      Array.isArray(actions) && actions.some((a) => a?.__typename === 'GuildAvailabilityAction')

    if (banner.type === 'tickets') {
      return {
        ctaText: t('getShowtimes', 'Get Showtimes'),
        icon: <TicketIcon size={22} color={'core-gray-950'} />,
        href: `${paths.tickets.index}/${banner.project?.slug}`,
      }
    } else if (banner.type === 'streaming') {
      if (banner.project?.title?.__typename === 'ContentSeries') {
        if (hasGuildAction(banner.project?.title?.firstWatchable?.actionsToWatch)) {
          return {
            ctaText: t('joinToWatch', 'Join To Watch'),
            icon: <GuildIconMono size={22} color={'core-gray-950'} />,
            href: `${paths.guild.join}/${banner.project?.slug}`,
          }
        } else {
          return {
            ctaText: t('watchNow', 'Watch Now'),
            icon: <PlayCircleOutlineIcon color={'core-gray-950'} />,
            href: `${paths.watch.index}/${banner.project?.slug}`,
          }
        }
      } else if (banner.project?.title?.__typename === 'ContentMovie') {
        if (hasGuildAction(banner.project?.title?.actionsToWatch)) {
          return {
            ctaText: t('joinToWatch', 'Join To Watch'),
            icon: <GuildIconMono size={22} color={'core-gray-950'} />,
            href: `${paths.guild.join}/${banner.project?.slug}`,
          }
        } else {
          return {
            ctaText: t('watchNow', 'Watch Now'),
            icon: <PlayCircleOutlineIcon color={'core-gray-950'} />,
            href: `${paths.watch.index}/${banner.project?.slug}`,
          }
        }
      }
    } else {
      return {
        ctaText: banner.genericConfig?.cta || '',
        icon: null,
        href: banner?.genericConfig?.link ? banner.genericConfig.link : `${paths.watch.index}/${banner.project.slug}`,
      }
    }
  }, [currentSlide, heroBanners, t])

  const logoPath = useMemo(() => {
    const banner = heroBanners[currentSlide]
    if (!banner) return ''

    if (banner.type === 'generic') {
      return (
        banner?.genericConfig?.logoCloudinaryPath ??
        banner?.project?.title?.logo?.cloudinaryPath ??
        banner?.project?.logoCloudinaryPath
      )
    } else {
      return banner?.project?.title?.logo?.cloudinaryPath ?? banner?.project?.logoCloudinaryPath
    }
  }, [currentSlide, heroBanners])

  return (
    <div
      className="group-watch-banner relative -mx-4 flex flex-col sm:-mx-8 md:-mx-12 xl:-mx-16"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Fragment key={currentSlide}>
        <Transition
          show
          appear
          enter="transition-opacity duration-1000"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-5500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={classNames([
              'flex flex-col gap-4 relative w-full lg:items-center min-h-[60vh] lg:flex-row lg:flex-wrap md:min-h-[45vh] lg:min-h-[50vh] xl:min-h-[53vh] bg-transparent',
              className,
            ])}
            ref={contentRef}
          >
            <PaddedContainer fluid className="flex items-center justify-center">
              <div className="relative mt-[23vh] w-full pt-24 md:mt-6 lg:mt-10 lg:max-w-[600px] xl:max-w-[650px]">
                <BoundedImage
                  className="mb-4 flex min-h-[64px] items-center justify-center md:mb-2 md:justify-start"
                  alt={heroBanners[currentSlide]?.project?.name ?? ''}
                  path={logoPath}
                  width={600}
                  height={104}
                  imageClassName="se:max-h-[64px]"
                />
                <div className="mb-2 lg:mb-4">
                  <CaptionMD
                    className="min-h-9 text-center md:max-w-[420px] md:text-left lg:min-h-[18px] lg:max-w-[600px] xl:max-w-[650px]"
                    weight="medium"
                  >
                    {heroBanners[currentSlide]?.flavorText}
                  </CaptionMD>
                </div>
                <div className="mb-2 flex flex-row flex-wrap items-center justify-center gap-1 md:justify-start lg:mb-4 lg:gap-2">
                  {heroBanners[currentSlide]?.project?.releaseDate && (
                    <>
                      <CaptionSM className="sm:photon-caption-md">
                        {DateTime.fromISO(heroBanners[currentSlide]?.project?.releaseDate).year}
                      </CaptionSM>
                      <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                    </>
                  )}
                  <CaptionSM className="sm:photon-caption-md whitespace-nowrap">
                    <Switch>
                      <Case condition={heroBanners[currentSlide]?.project?.projectType === projectTypes.series}>
                        <Translate i18nKey="tvSeries" t={t}>
                          TV Series
                        </Translate>
                      </Case>
                      <Case condition={heroBanners[currentSlide]?.project?.projectType === projectTypes.movie}>
                        <Translate i18nKey="movie" t={t}>
                          Movie
                        </Translate>
                      </Case>
                      <Case condition={heroBanners[currentSlide]?.project?.projectType === projectTypes.podcast}>
                        <Translate i18nKey="podcast" t={t}>
                          Podcast
                        </Translate>
                      </Case>
                      <Case condition={heroBanners[currentSlide]?.project?.projectType === projectTypes.special}>
                        <Translate i18nKey="special" t={t}>
                          Special
                        </Translate>
                      </Case>
                    </Switch>
                  </CaptionSM>
                  {(heroBanners[currentSlide]?.project?.title?.genres?.length ?? 0) > 0 && (
                    <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                  )}
                  {heroBanners[currentSlide]?.project?.title?.genres?.map((genre: string | null, index: number) => {
                    return (
                      <div className="flex gap-1 lg:gap-2" key={genre}>
                        <CaptionSM className="sm:photon-caption-md">{genre}</CaptionSM>
                        <When condition={index < (heroBanners[currentSlide]?.project?.title?.genres?.length ?? 0) - 1}>
                          <CaptionSM className="sm:photon-caption-md">•</CaptionSM>
                        </When>
                      </div>
                    )
                  })}
                </div>
                <div className="md:min-h-auto mb-4 flex min-h-[47px] flex-row flex-wrap items-center justify-center gap-2 md:justify-start lg:mb-8 lg:min-h-7">
                  {heroBanners[currentSlide]?.project?.title?.ratings?.[0] && (
                    <ContentRatingBadge
                      contentRating={heroBanners[currentSlide]?.project?.title?.ratings?.[0]}
                      className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                    />
                  )}
                  <HighDefinitionBadge
                    slug={heroBanners[currentSlide]?.project?.slug}
                    className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl"
                  />
                  <ClosedCaptionsBadge className="rounded-xl border-0 bg-[#0000004d] px-3 py-1 text-white backdrop-blur-xl" />
                  <When condition={heroBanners[currentSlide]?.project?.highestScore}>
                    <GuildScore guildScore={heroBanners[currentSlide]?.project?.highestScore} hideAnimate />
                  </When>
                </div>
                <LinkButton
                  href={ctaButtonProps?.href ?? ''}
                  variant="white"
                  onClick={() => trackClick(heroBanners[currentSlide]?.recommendationId)}
                  className="flex max-h-12 w-full items-center gap-[6px] rounded-xl px-6 py-[14px] md:w-fit"
                >
                  <LabelMD
                    weight="semibold"
                    className="mt-[2.25px] flex flex-row items-center justify-center gap-2"
                    color="core-gray-950"
                  >
                    {ctaButtonProps?.icon}
                    {ctaButtonProps?.ctaText}
                  </LabelMD>
                </LinkButton>
              </div>
            </PaddedContainer>
          </div>
        </Transition>
      </Fragment>
      <div className="mt-4 flex w-full items-center justify-center md:mt-0">
        <ul className="flex h-full min-w-[98px] flex-row gap-1 md:items-end md:pb-2">
          {heroBanners?.map((item, index) => (
            <li
              key={item.id || index}
              onClick={() => setCurrentSlide(index)}
              className="flex cursor-pointer items-center justify-center p-1"
            >
              <Transition
                show={currentSlide === index}
                className="max-h-[10px] overflow-hidden rounded-full"
                enter="transition ease-in-out duration-850"
                enterFrom="-translate-x-4 scale-x-0 w-2.5 bg-gray-500"
                enterTo="translate-x-0 scale-x-1 w-8 bg-white"
              >
                <div
                  className={classNames('bg-white max-h-2.5 h-2.5 w-2.5 rounded-full ', {
                    '!w-8': currentSlide === index,
                  })}
                  key={item.id || index}
                />
              </Transition>
              {currentSlide !== index && (
                <div
                  className={classNames('h-2.5 w-2.5 rounded-full bg-gray-500', {
                    'bg-white !w-8': currentSlide === index,
                  })}
                  key={item.id || index}
                />
              )}
            </li>
          ))}
        </ul>
      </div>
      <div className="absolute bottom-2 right-0 hidden min-w-10 lg:mx-12 lg:flex xl:mx-16">
        <div className="relative opacity-0 transition-all duration-300 ease-in-out group-watch-banner-hover:opacity-100">
          <div className="flex flex-row gap-4">
            <Button
              variant="plain"
              onClick={prevSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll right`}
            >
              <CaretRightIcon size={26} color={'white'} className="rotate-180" />
            </Button>
            <Button
              variant="plain"
              onClick={nextSlide}
              className={classNames(
                'pointer-events-auto rounded-full p-2 bg-core-gray-900 opacity-55 hover:opacity-100 transition-all ease-in-out duration-300',
              )}
              aria-label={`Scroll right`}
            >
              <CaretRightIcon size={26} color={'white'} className="" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
